import { supabase } from '../lib/supabase';

/**
 * Review Service - Handles all review-related operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

export const reviewService = {
  /**
   * Get all reviews with product info and images
   * @param {Object} filters - Optional filters (rating, isVerified, productId)
   * @param {number} limit - Number of reviews to fetch
   * @param {number} offset - Offset for pagination
   */
  async getAllReviews({ rating, isVerified, productId } = {}, limit = 20, offset = 0) {
    try {
      let query = supabase?.from('reviews')?.select(`
          *,
          review_images(*),
          products(
            id,
            name,
            slug,
            brand,
            image_url,
            image_alt,
            category
          )
        `)?.order('created_at', { ascending: false })?.range(offset, offset + limit - 1);

      // Apply filters if provided
      if (rating) {
        query = query?.eq('rating', rating);
      }
      // CRITICAL FIX: Only apply isVerified filter when it's explicitly true or false, not null
      if (typeof isVerified === 'boolean') {
        query = query?.eq('is_verified', isVerified);
      }
      if (productId) {
        query = query?.eq('product_id', productId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  /**
   * Get review by ID with all related data
   */
  async getReviewById(reviewId) {
    try {
      const { data, error } = await supabase?.from('reviews')?.select(`*,review_images(*),products(id,name,slug,brand,image_url,image_alt,category,current_price,original_price,discount,badge)`)?.eq('id', reviewId)?.single();

      if (error) throw error;
      if (!data) return null;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching review:', error);
      throw error;
    }
  },

  /**
   * Get reviews count by rating
   */
  async getReviewsStats(productId) {
    try {
      let query = supabase?.from('reviews')?.select('rating', { count: 'exact' });

      if (productId) {
        query = query?.eq('product_id', productId);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Calculate rating distribution
      const ratingCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      data?.forEach(review => {
        if (review?.rating >= 1 && review?.rating <= 5) {
          ratingCounts[review.rating]++;
        }
      });

      // Calculate average rating
      const totalRatings = data?.reduce((sum, r) => sum + r?.rating, 0) || 0;
      const averageRating = data?.length > 0 ? totalRatings / data?.length : 0;

      return {
        totalReviews: count || 0,
        averageRating: Number(averageRating?.toFixed(1)),
        ratingDistribution: ratingCounts
      };
    } catch (error) {
      console.error('Error fetching review stats:', error);
      throw error;
    }
  },

  /**
   * Increment helpful count for a review
   */
  async markReviewHelpful(reviewId) {
    try {
      // First, get current helpful count
      const { data: review, error: fetchError } = await supabase?.from('reviews')?.select('helpful_count')?.eq('id', reviewId)?.single();

      if (fetchError) throw fetchError;

      // Increment the count
      const { data, error } = await supabase?.from('reviews')?.update({ helpful_count: (review?.helpful_count || 0) + 1 })?.eq('id', reviewId)?.select()?.single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error('Error marking review helpful:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time updates for a specific review
   */
  subscribeToReview(reviewId, callback) {
    const channel = supabase?.channel(`review-${reviewId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reviews',
          filter: `id=eq.${reviewId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};

export default reviewService;